import { FC, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { CloseIcon, YellowWarningIcon } from '@repo/atomic-components';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { useAppSelector, selectMetadata } from '@/store';

/**
 * AiProcessingFailedDialog
 * Pops up automatically when the user opens the V2 GDPR check page on a document
 * whose AI processing has failed (`metadata.aiChecked === false`). Replaces the
 * old red top banner with a one-shot warning popup styled to match the bundle
 * screen's "revert sort" confirmation dialog (custom 280px shell, yellow warning
 * icon centred, 24px padding).
 */
export const AiProcessingFailedDialog: FC = () => {
  const metadata = useAppSelector(selectMetadata);
  const [visible, setVisible] = useState(false);

  // Show once metadata loads and aiChecked is false. The effect refires if the
  // user navigates to a different document that also has aiChecked=false.
  useEffect(() => {
    if (metadata && !metadata.aiChecked) {
      setVisible(true);
    }
  }, [metadata]);

  const handleClose = () => setVisible(false);

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '280px', borderRadius: '8px', border: '1px solid #e1e1e0', overflow: 'hidden' }}
      contentClassName="!p-0"
      headerClassName="!hidden"
    >
      <div className="relative p-[24px]">
        <button
          type="button"
          aria-label="Close"
          onClick={handleClose}
          className="absolute top-[24px] right-[24px] cursor-pointer rounded p-1 focus:outline-none"
        >
          <CloseIcon width={12} height={12} />
        </button>
        <div className="mt-[16px] flex flex-col items-center">
          <YellowWarningIcon width={48} height={48} />
          <div className="text-fletchers-black-900 mt-[16px] text-[18px]/[18px] font-[600]">AI processing failed</div>
        </div>
        <div className="mt-[16px]">
          <p className="text-center text-[14px]/[20px]">
            This document couldn’t be processed by AI. A manual GDPR check is required.
          </p>
        </div>
        <div className="mt-[24px] flex justify-end">
          <OutlineButton className="!h-[32px]" onClick={handleClose}>
            Dismiss
          </OutlineButton>
        </div>
      </div>
    </Dialog>
  );
};
